import React, { createContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { createSignalRConnector } from '../signalr/createSignalRConnector';
import { createStopAllConnections, createStopConnection } from '../signalr/stopConnections';

type SignalRModelState = {
  chatId: string | null;
  history: any[];
  submittedClarifications: Record<string, boolean>;
  clarificationsToSend?: any;
};

type SignalRConsumer = {
  getUserToken: () => string | undefined | Promise<string | undefined>;
  onIncoming?: (chatKey: string, payload: any, eventName?: string) => void;
  clearState?: (chatKey: string) => void;
};

type SignalRContextValue = {
  connectSignalR: (consumer: SignalRConsumer, chatKey: string) => Promise<void>;
  stopConnection: (chatKey: string) => Promise<void>;
  stopAllConnections: (exceptId?: string) => Promise<void>;
  clearSignalRState: (chatKey: string) => void;
  modelState?: SignalRModelState;
  onModelUpdated?: (payload: SignalRModelState) => void;
};

const SignalRContext = createContext<SignalRContextValue | null>(null);

type SignalRProviderProps = {
  incomingConsumer?: SignalRConsumer;
  onReady?: (api: SignalRContextValue) => void;
  modelState?: SignalRModelState;
  onModelUpdated?: (payload: SignalRModelState) => void;
  children: React.ReactNode;
};

export const SignalRProvider = ({
  incomingConsumer,
  onReady,
  modelState,
  onModelUpdated,
  children,
}: SignalRProviderProps) => {
  const connectionsRef = useRef(new Map());
  const incomingConsumerRef = useRef<SignalRConsumer | undefined>(incomingConsumer);

  useEffect(() => { incomingConsumerRef.current = incomingConsumer; }, [incomingConsumer]);

  const handleIncoming = useCallback((chatKey: string, payload: any, eventName?: string) => {
    incomingConsumerRef.current?.onIncoming?.(chatKey, payload, eventName);
  }, []);

  const connectSignalR = useMemo(() => createSignalRConnector({ connectionsRef, handleIncoming }), [handleIncoming]);
  const stopConnection = useMemo(() => createStopConnection(connectionsRef), []);
  const stopAllConnections = useMemo(() => createStopAllConnections(connectionsRef), []);

  const clearSignalRState = useCallback((id: string) => {
    incomingConsumerRef.current?.clearState?.(id);
  }, []);

  const value = useMemo(() => ({
    connectSignalR,
    stopConnection,
    stopAllConnections,
    clearSignalRState,
    modelState,
    onModelUpdated,
  }), [connectSignalR, stopAllConnections, stopConnection, clearSignalRState, modelState, onModelUpdated]);

  useEffect(() => { if (onReady) onReady(value); }, [onReady, value]);

  return <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>;
};

export const useSignalRContext = () => {
  const ctx = React.useContext(SignalRContext);
  if (!ctx) {
    throw new Error('useSignalRContext must be used within SignalRProvider');
  }
  return ctx;
};
